import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { CircularProgress } from '@material-ui/core/';
import { parseISO, format } from 'date-fns';
import { RouteComponentProps, useParams } from 'react-router';
import {
  BackTo,
  Typography,
  Card,
  Tables,
  showToast,
} from '@deeptrue-frontend/ui';
import api from 'api/api';
import { useFormatMessage } from 'components/IntlProvider';
import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import Forms from 'components/Forms';
import { FormikHelpers } from 'formik';

const { Table, TableBody, TableCell, TableRow, TableHeader } = Tables;
const { connect, selectors, actions } = StoreProvider;

const CreditsCardsContainer = styled.div`
width: 100%
align-items: space-between;
  display: flex;
`;
const CreditsInputContainer = styled.div`
  display: flex;
  margin: 1.5rem 0;
  label {
    width: 20rem;
  }
`;
const OrganizationCreditsContent = styled.div`
  display: flex;
  flex-direction: column;
`;
const StyledCard = styled(Card)`
  width: 20rem;
  margin-top: 2rem;
  margin-right: 2rem;
  h5 {
    font-size: 1rem;
    font-weight: 500;
  }
`;

const CenteredDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: ctenr;
`;

interface Props extends RouteComponentProps {
  credits: any;
}

const defineBalanceActionTypeName = (type: 1 | 2 | 3 | 4 | 5 | 6, t: any) => {
  switch (type) {
    case 1:
      return t(
        'organizations.creditsManagement.tokens.history.status.bought',
        'Bought'
      );
    case 2:
      return t(
        'organizations.creditsManagement.tokens.history.status.spent',
        'Spent'
      );
    case 3:
      return t(
        'organizations.creditsManagement.tokens.history.status.refunded',
        'Refunded'
      );
    case 4:
      return t(
        'organizations.creditsManagement.tokens.history.status.granted',
        'Granted'
      );
    case 5:
      return t(
        'organizations.creditsManagement.tokens.history.status.reserved',
        'Reserved'
      );
    case 6:
      return t(
        'organizations.creditsManagement.tokens.history.status.donate',
        'Raised'
      );
    default:
      return '';
  }
};

type HistoryType = {
  created_at: string;
  id: number;
  quantity: number;
  saldo: number;
  type: 1 | 2 | 3 | 4 | 5 | 6;
  type_name: string;
  user_uuid: string;
};
interface FormValues {
  tokens: number;
}
interface CurrentCreditsType {
  currentBallance: number;
  reservedCredits: number;
  balanceHistory: HistoryType[];
  isLoading: boolean;
}

interface OrganizationDetailsType {
  codename: string;
  name: string;
}

const OrganizationCredits = ({ location, history }: Props) => {
  const { id } = useParams<{ id: string }>();
  const [currentCredits, setCurrentCredits] = useState<CurrentCreditsType>({
    currentBallance: 0,
    reservedCredits: 0,
    balanceHistory: [],
    isLoading: true,
  });

  const [organizationDetails, setOrganizationDetails] = useState<
    OrganizationDetailsType
  >({
    codename: '',
    name: '',
  });

  const t = useFormatMessage();

  const raiseTokens = (
    value: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) => {
    const { tokens } = value;

    return api.resources.payments
      .donateOrgToken({
        orgName: organizationDetails.codename,
        tokens: tokens,
      })
      .then(({ data }: { data: HistoryType }) => {
        const {
          currentBallance,
          reservedCredits,
          balanceHistory,
        } = currentCredits;

        setCurrentCredits({
          currentBallance: currentBallance + Number(tokens),
          reservedCredits: reservedCredits,
          balanceHistory: [data, ...balanceHistory],
          isLoading: false,
        });
        resetForm({});

        showToast({
          type: 'success',
          title: t(
            'organizations.creditsManagement.donate.token.success',
            'Tokens added succesfully'
          ),
        });
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'organizations.creditsManagement.donate.token.failure',
            'We spotted an error during token donation.'
          ),
        });
      });
  };

  useEffect(() => {
    api.resources
      .getOrganization({ id: id })
      .then(({ data }) => {
        const { codename, name } = data;

        Promise.all([
          api.resources.payments.getOrganizationBalanceHistory({
            orgName: codename,
          }),
          api.resources.getOrganizationPaymentLimits(codename),
        ])
          .then(([balance, limits]) => {
            setOrganizationDetails({
              name: name,
              codename: codename,
            });

            setCurrentCredits({
              currentBallance: limits.data.available_tokens,
              reservedCredits: limits.data.reserved_tokens,
              balanceHistory: balance.data.results,
              isLoading: false,
            });
          })
          .catch(() => {
            showToast({
              type: 'error',
              title: t(
                'organizations.creditsManagement.error',
                'Something went wrong. Please contact administrator'
              ),
            });
            setCurrentCredits({
              currentBallance: 0,
              reservedCredits: 0,
              balanceHistory: [],
              isLoading: false,
            });
          });
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'organizations.creditsManagement.error',
            'Something went wrong. Please contact administrator'
          ),
        });

        setCurrentCredits({
          currentBallance: 0,
          reservedCredits: 0,
          balanceHistory: [],
          isLoading: false,
        });
      });

    // eslint-disable-next-line
  }, []);

  const areHistoryData = currentCredits.balanceHistory.length > 0;

  return (
    <Dashboard
      title={t('organizations.creditsManagement.title', 'Credits management')}
      location={location}
    >
      <BackTo onClick={() => history.push('/admin/organizations')}>
        {t('backTo.organizations', 'Back to Organizations')}
      </BackTo>
      <OrganizationCreditsContent>
        <Typography variant={'h6'}>
          {t(
            'organizations.creditsManagement.subtitle',
            'Organization {org} credits',
            {
              org: organizationDetails.name,
            }
          )}
        </Typography>

        <CreditsCardsContainer>
          <StyledCard
            title={t(
              'organizations.creditsManagement.current_balance',
              'Current Balance'
            )}
          >
            {currentCredits.isLoading ? (
              <CircularProgress size="1rem" />
            ) : (
              currentCredits.currentBallance
            )}
          </StyledCard>
          <StyledCard
            title={t(
              'organizations.creditsManagement.current_balance',
              'Reserved Credits'
            )}
          >
            {currentCredits.isLoading ? (
              <CircularProgress size="1rem" />
            ) : (
              currentCredits.reservedCredits
            )}
          </StyledCard>
        </CreditsCardsContainer>
        <CreditsInputContainer>
          <Forms.TokenRaise
            onSubmit={raiseTokens}
            initialValues={{ tokens: 0 }}
          />
        </CreditsInputContainer>

        <Typography
          mb="1rem"
          fontSize="1.125rem"
          color="253858"
          fontWeight="500"
        >
          {t('organizations.creditsManagement.balanceTitle', 'Balance History')}
        </Typography>
        <Table>
          <TableHeader
            cells={[
              t('organizations.creditsManagement.history.table.date', 'Date'),
              t(
                'organizations.creditsManagement.history.table.amount',
                'Amount'
              ),
              t('organizations.creditsManagement.history.table.type', 'Type'),
              t(
                'organizations.creditsManagement.history.table.balance',
                'Balance'
              ),
            ]}
          />
          <TableBody>
            {currentCredits.isLoading ? (
              <TableRow>
                <TableCell colSpan="4">
                  <CenteredDiv>
                    <CircularProgress size="2rem" />
                  </CenteredDiv>
                </TableCell>
              </TableRow>
            ) : areHistoryData ? (
              currentCredits.balanceHistory.map((history: HistoryType) => (
                <TableRow key={history.id}>
                  <TableCell>
                    {format(parseISO(history.created_at), 'dd-MM-yyyy HH:mm')}
                  </TableCell>
                  <TableCell>{history.quantity}</TableCell>
                  <TableCell>
                    {defineBalanceActionTypeName(history.type, t)}
                  </TableCell>
                  <TableCell>{history.saldo}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan="4">
                  <CenteredDiv>
                    {t(
                      'organizations.creditsManagement.history.nodata',
                      'No historic data'
                    )}
                  </CenteredDiv>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </OrganizationCreditsContent>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    credits: selectors.organizationsSelectors.credits(state),
  }),
  {
    setOrganizationCredits: actions.organizationsActions.setOrganizationCredits,
  }
)(OrganizationCredits);
